import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/ui-components/ui/dialog";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/ui-components/ui/card";
import { CheckCircle2, Flame, Loader2 } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils/cn";

interface ChangePlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: "basic" | "pro" | "custom";
  onSelectPlan: (plan: "basic" | "pro") => Promise<void>;
  hasUsedTrial?: boolean;
}

const BASIC_FEATURES = [
  "Unbegrenzte Social Media Posts",
  "Zugriff auf alle Templates",
  "Posten auf Instagram, Facebook & WhatsApp",
  "Planung & Auto-Posting",
  "Sponsoren-Management",
];

const PRO_FEATURES = [
  { text: "Alles im Basic-Paket", highlight: true },
  "Eigene Templates erstellen und bearbeiten",
  "Posts ohne Wasserzeichen",
  { text: "KI Bildunterschrift" },
  { text: "KI Hintergrund-Entfernung" },
  { text: "Analytics & Insights", badge: "Bald" },
];

const TRIAL_BADGE = (
  <div className="mt-3 inline-flex items-center justify-center rounded-full bg-blue-50 px-3 py-1 text-[11px] font-semibold text-blue-600">
    14 Tage kostenlos testen
  </div>
);

export function ChangePlanDialog({ isOpen, onClose, currentPlan, onSelectPlan, hasUsedTrial }: ChangePlanDialogProps) {
  const [isLoading, setIsLoading] = useState<"basic" | "pro" | null>(null);

  const handleSelect = async (plan: "basic" | "pro") => {
    setIsLoading(plan);
    try {
      await onSelectPlan(plan);
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100%-2rem)] flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white p-0 shadow-xl sm:w-full sm:max-w-4xl">
        <div className="border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/60 px-6 py-6 text-center sm:px-8 sm:py-7">
          <DialogTitle className="text-2xl font-semibold tracking-tight text-gray-900">Wähle deinen Plan</DialogTitle>
          <DialogDescription className="mt-2 text-sm leading-relaxed text-gray-500">
            Upgrade oder Downgrade jederzeit möglich.
          </DialogDescription>
        </div>

        <div className="overflow-y-auto px-4 py-4 sm:px-6 sm:py-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5">
            <Card className={cn(
              "relative mt-2 flex h-full flex-col overflow-visible rounded-2xl border bg-white shadow-sm transition-all",
              currentPlan === "basic" ? "border-blue-200 ring-2 ring-blue-100" : "border-gray-200"
            )}>
              {currentPlan === "basic" && (
                <div className="absolute -top-3 left-1/2 z-20 -translate-x-1/2 rounded-full border border-blue-200 bg-blue-600 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm">
                  Aktuell
                </div>
              )}
              <CardHeader className="relative border-b border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50/40 px-6 pb-6 pt-8 text-center">
                <CardTitle className="mb-2 text-lg font-semibold text-gray-900">Basic</CardTitle>
                <div className="flex justify-center items-end text-gray-900">
                  <span className="text-4xl font-bold tracking-tight">29</span>
                  <span className="text-base font-medium text-gray-500 ml-1 mb-1">€/mtl.</span>
                </div>
                <CardDescription className="mt-2 text-sm text-gray-500">
                  Für Einsteiger.
                </CardDescription>
                {!hasUsedTrial && TRIAL_BADGE}
              </CardHeader>

              <CardContent className="flex-1 space-y-6 p-6 sm:p-7">
                <ul className="space-y-3.5">
                  {BASIC_FEATURES.map((feature, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600">
                      <CheckCircle2 className="mr-3 mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                      <span className="leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="mt-auto rounded-b-2xl bg-white p-6 pt-0 sm:p-7 sm:pt-0">
                <Button
                  variant="outline"
                  className="h-10 w-full rounded-xl border-gray-200 text-sm font-semibold text-gray-900 transition-all hover:bg-gray-50"
                  onClick={() => handleSelect('basic')}
                  disabled={currentPlan === "basic" || isLoading !== null}
                >
                  {isLoading === "basic" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wechsle Plan...
                    </>
                  ) : (
                    currentPlan === "basic" ? "Dein aktueller Plan" : (hasUsedTrial ? "Basic wählen" : "Basic kostenlos testen")
                  )}
                </Button>
              </CardFooter>
            </Card>

            <Card className={cn(
              "relative mt-2 flex h-full flex-col overflow-visible rounded-2xl border bg-white shadow-sm transition-all",
              currentPlan === "pro" ? "border-blue-200 ring-2 ring-blue-100" : "border-gray-200"
            )}>
              <div className="absolute right-4 top-4 z-10 flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-blue-600">
                <Flame className="w-3 h-3 fill-blue-600 text-blue-600" />
                Beliebt
              </div>

              {currentPlan === "pro" && (
                <div className="absolute -top-3 left-1/2 z-20 -translate-x-1/2 rounded-full border border-blue-200 bg-blue-600 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white shadow-sm">
                  Aktuell
                </div>
              )}

              <CardHeader className="relative rounded-t-2xl border-b border-blue-100/60 bg-gradient-to-br from-blue-50/70 via-white to-blue-50/30 px-6 pb-6 pt-8 text-center">
                <CardTitle className="mb-2 text-lg font-semibold text-blue-900">Pro Abo</CardTitle>
                <div className="flex justify-center items-end text-blue-700">
                  <span className="text-4xl font-bold tracking-tight">59</span>
                  <span className="text-base font-medium text-blue-600/80 ml-1 mb-1">€/mtl.</span>
                </div>
                {!hasUsedTrial && TRIAL_BADGE}
              </CardHeader>

              <CardContent className="flex-1 space-y-6 p-6 sm:p-7">
                <ul className="space-y-3.5">
                  {PRO_FEATURES.map((feature, i) => (
                    <li key={i} className="flex items-start text-sm text-gray-600">
                      <CheckCircle2 className="mr-3 mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
                      <span className="leading-relaxed">
                        {typeof feature === 'string' ? feature : (
                          <span className={cn(feature.highlight && "font-medium text-gray-900")}>
                            {feature.text}
                            {feature.badge && (
                              <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                {feature.badge}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="mt-auto rounded-b-2xl bg-white p-6 pt-0 sm:p-7 sm:pt-0">
                <Button
                  className="h-10 w-full rounded-xl bg-blue-600 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700"
                  onClick={() => handleSelect('pro')}
                  disabled={currentPlan === "pro" || isLoading !== null}
                >
                  {isLoading === "pro" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wechsle Plan...
                    </>
                  ) : (
                    currentPlan === "pro" ? "Dein aktueller Plan" : (hasUsedTrial ? "Pro wählen" : "Pro kostenlos testen")
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
